'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { forgeFoodService, ForgeFood } from '@/lib/services/forgeFoodService';
import { forgeGoalsService, ForgeFavouriteFood } from '@/lib/services/forgeGoalsService';
import { createClient } from '@/lib/supabase/client';
import toast from 'react-hot-toast';

interface FoodSearchPanelProps {
  onAddFood?: (food: { name: string; grams: number; cal: number; protein: number; carbs: number; fat: number }, mealId: string) => void;
  meals: { id: string; name: string }[];
}

type Tab = 'hunters' | 'favourites';

export default function FoodSearchPanel({ onAddFood, meals }: FoodSearchPanelProps) {
  const [tab, setTab] = useState<Tab>('hunters');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ForgeFood[]>([]);
  const [allFoods, setAllFoods] = useState<ForgeFood[]>([]);
  const [favourites, setFavourites] = useState<ForgeFavouriteFood[]>([]);
  const [selectedGrams, setSelectedGrams] = useState<Record<string, number>>({});
  const [selectedMealId, setSelectedMealId] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualFood, setManualFood] = useState({ name: '', cal100: '', protein100: '', carbs100: '', fat100: '', servingSize: '100', servingUnit: 'g' });
  const [isSavingManual, setIsSavingManual] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      setUserId(user.id);
      const foods = await forgeFoodService.getAllFoods();
      setAllFoods(foods);
      const favs = await forgeGoalsService.getFavouriteFoods(user.id);
      setFavourites(favs);
    });
  }, []);

  useEffect(() => {
    if (meals.length > 0 && !selectedMealId) {
      setSelectedMealId(meals[0].id);
    }
  }, [meals, selectedMealId]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    setIsSearching(true);
    const timeout = setTimeout(async () => {
      const found = await forgeFoodService.searchFoods(query);
      setResults(found);
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  function getGrams(id: string, defaultG: number) {
    return selectedGrams[id] ?? defaultG;
  }

  function calcNutrient(per100: number, grams: number) {
    return Math.round((per100 * grams) / 100);
  }

  async function handleAddFromHunters(food: ForgeFood) {
    if (!onAddFood || !selectedMealId) return;
    const grams = getGrams(food.id, food.servingSizeG);
    const cal = calcNutrient(food.calPer100g, grams);
    const protein = calcNutrient(food.proteinPer100g, grams);
    const carbs = calcNutrient(food.carbsPer100g, grams);
    const fat = calcNutrient(food.fatPer100g, grams);
    onAddFood({ name: food.name, grams, cal, protein, carbs, fat }, selectedMealId);
    try {
      await forgeFoodService.logFood({
        foodId: food.id,
        foodName: food.name,
        mealType: selectedMealId,
        grams,
        cal,
        proteinG: protein,
        carbsG: carbs,
        fatG: fat,
      });
    } catch { /* non-blocking */ }
    toast.success(`${food.name} added`);
  }

  async function handleAddFavourite(fav: ForgeFavouriteFood) {
    if (!onAddFood || !selectedMealId) return;
    onAddFood({
      name: fav.foodName,
      grams: fav.servingSizeG,
      cal: fav.calPerServing,
      protein: fav.proteinPerServing,
      carbs: fav.carbsPerServing,
      fat: fav.fatPerServing,
    }, selectedMealId);
    try {
      await forgeFoodService.logFood({
        foodId: fav.foodId,
        foodName: fav.foodName,
        mealType: selectedMealId,
        grams: fav.servingSizeG,
        cal: fav.calPerServing,
        proteinG: fav.proteinPerServing,
        carbsG: fav.carbsPerServing,
        fatG: fav.fatPerServing,
      });
    } catch { /* non-blocking */ }
    toast.success(`${fav.foodName} added`);
  }

  async function handleRemoveFavourite(favId: string) {
    if (!userId) return;
    await forgeGoalsService.removeFavouriteFood(userId, favId);
    setFavourites((prev) => prev.filter((f) => f.id !== favId));
    toast.success('Removed from favourites');
  }

  async function handleAddToFavourites(food: ForgeFood) {
    if (!userId) return;
    const fav = await forgeGoalsService.addFavouriteFood(userId, {
      foodId: food.id,
      foodName: food.name,
      calPerServing: food.calPerServing,
      proteinPerServing: food.proteinPerServing,
      carbsPerServing: food.carbsPerServing,
      fatPerServing: food.fatPerServing,
      servingSizeG: food.servingSizeG,
      servingUnit: food.servingUnit,
    });
    if (fav) {
      setFavourites((prev) => [fav, ...prev.filter((f) => f.foodId !== food.id)]);
      toast.success(`${food.name} added to favourites ⭐`);
    }
  }

  async function handleSaveManual() {
    if (!manualFood.name.trim() || !manualFood.cal100) return;
    setIsSavingManual(true);
    try {
      const saved = await forgeFoodService.saveFood({
        name: manualFood.name.trim(),
        servingSizeG: parseFloat(manualFood.servingSize) || 100,
        servingUnit: manualFood.servingUnit || 'g',
        calPer100g: parseFloat(manualFood.cal100) || 0,
        proteinPer100g: parseFloat(manualFood.protein100) || 0,
        carbsPer100g: parseFloat(manualFood.carbs100) || 0,
        fatPer100g: parseFloat(manualFood.fat100) || 0,
        fibrePer100g: 0,
        calPerServing: Math.round((parseFloat(manualFood.cal100) || 0) * (parseFloat(manualFood.servingSize) || 100) / 100),
        proteinPerServing: Math.round((parseFloat(manualFood.protein100) || 0) * (parseFloat(manualFood.servingSize) || 100) / 100),
        carbsPerServing: Math.round((parseFloat(manualFood.carbs100) || 0) * (parseFloat(manualFood.servingSize) || 100) / 100),
        fatPerServing: Math.round((parseFloat(manualFood.fat100) || 0) * (parseFloat(manualFood.servingSize) || 100) / 100),
        fibrePerServing: 0,
        confidence: 'medium',
        notes: 'Manually added',
      });
      if (saved) {
        setAllFoods((prev) => [saved, ...prev]);
        toast.success(`${saved.name} saved to Hunter's List!`);
        setManualFood({ name: '', cal100: '', protein100: '', carbs100: '', fat100: '', servingSize: '100', servingUnit: 'g' });
        setShowAddManual(false);
      }
    } catch {
      toast.error('Could not save food. Try again.');
    } finally {
      setIsSavingManual(false);
    }
  }

  const displayFoods = query.length >= 2 ? results : allFoods.slice(0, 20);

  return (
    <div className="space-y-4">
      {/* Meal selector */}
      {meals.length > 0 && (
        <div className="card-forge rounded-xl p-3">
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-2">Add to Meal</div>
          <select
            value={selectedMealId}
            onChange={(e) => setSelectedMealId(e.target.value)}
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50"
          >
            {meals.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="card-forge rounded-xl overflow-hidden">
        <div className="flex border-b border-border">
          {(['hunters', 'favourites'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'hunters' ? "🎯 Hunter's List" : '⭐ Favourites'}
            </button>
          ))}
        </div>

        {/* Hunter's List */}
        {tab === 'hunters' && (
          <div className="p-4 space-y-3">
            <div className="relative">
              <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your food library..."
                className="w-full bg-input border border-border rounded-md pl-9 pr-4 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
              />
            </div>

            {isSearching && (
              <div className="text-center py-4 text-xs text-muted-foreground">Searching...</div>
            )}

            {!isSearching && displayFoods.length === 0 && (
              <div className="text-center py-8">
                <div className="text-3xl mb-3">🎯</div>
                <div className="text-sm font-bold text-foreground mb-1">Hunter&apos;s List is Empty</div>
                <div className="text-xs text-muted-foreground mb-4">
                  Search foods via AI and save them here, or add manually below.
                </div>
                <button
                  onClick={() => setShowAddManual(true)}
                  className="btn-forge-primary px-4 py-2 rounded-md text-xs font-bold"
                >
                  + ADD FOOD MANUALLY
                </button>
              </div>
            )}

            {!isSearching && displayFoods.length > 0 && (
              <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-forge">
                {displayFoods.map((food) => {
                  const grams = getGrams(food.id, food.servingSizeG);
                  return (
                    <div key={food.id} className="rounded-lg p-3 hover:bg-card-elevated transition-colors border border-transparent hover:border-border/50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{food.name}</div>
                          <div className="text-2xs text-muted-foreground font-tabular">
                            {food.calPer100g} kcal · P: {food.proteinPer100g}g · C: {food.carbsPer100g}g · F: {food.fatPer100g}g per 100g
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddToFavourites(food)}
                          title="Add to favourites"
                          className="text-muted-foreground hover:text-amber-400 transition-colors flex-shrink-0"
                        >
                          <Icon name="StarIcon" size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={grams}
                          onChange={(e) => setSelectedGrams((prev) => ({ ...prev, [food.id]: Number(e.target.value) }))}
                          className="w-16 bg-input border border-border rounded px-2 py-1 text-xs text-foreground font-tabular focus:outline-none focus:border-primary/50 text-center"
                          min={1}
                          inputMode="numeric"
                        />
                        <span className="text-2xs text-muted-foreground">g</span>
                        <div className="text-xs font-tabular text-muted-foreground flex-1">
                          = {calcNutrient(food.calPer100g, grams)} kcal
                        </div>
                        <button
                          onClick={() => handleAddFromHunters(food)}
                          className="btn-forge-primary px-3 py-1.5 rounded text-xs font-bold"
                        >
                          + ADD
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add manually button (when foods exist) */}
            {displayFoods.length > 0 && (
              <button
                onClick={() => setShowAddManual(true)}
                className="w-full py-2 rounded-md border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-all text-xs font-bold tracking-wider flex items-center justify-center gap-2"
              >
                <Icon name="PlusIcon" size={13} />
                ADD FOOD MANUALLY
              </button>
            )}

            {/* Manual add form */}
            {showAddManual && (
              <div className="bg-card-elevated rounded-xl p-4 border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-foreground uppercase tracking-wider">Add Food Manually</div>
                  <button onClick={() => setShowAddManual(false)} className="text-muted-foreground hover:text-foreground">
                    <Icon name="XMarkIcon" size={14} />
                  </button>
                </div>
                <input
                  type="text"
                  value={manualFood.name}
                  onChange={(e) => setManualFood((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Food name *"
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-2xs text-muted-foreground block mb-1">Calories per 100g *</label>
                    <input type="number" value={manualFood.cal100} onChange={(e) => setManualFood((p) => ({ ...p, cal100: e.target.value }))} placeholder="e.g. 165" inputMode="decimal" className="w-full bg-input border border-border rounded px-2 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-2xs text-muted-foreground block mb-1">Protein per 100g</label>
                    <input type="number" value={manualFood.protein100} onChange={(e) => setManualFood((p) => ({ ...p, protein100: e.target.value }))} placeholder="e.g. 31" inputMode="decimal" className="w-full bg-input border border-border rounded px-2 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-2xs text-muted-foreground block mb-1">Carbs per 100g</label>
                    <input type="number" value={manualFood.carbs100} onChange={(e) => setManualFood((p) => ({ ...p, carbs100: e.target.value }))} placeholder="e.g. 0" inputMode="decimal" className="w-full bg-input border border-border rounded px-2 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-2xs text-muted-foreground block mb-1">Fat per 100g</label>
                    <input type="number" value={manualFood.fat100} onChange={(e) => setManualFood((p) => ({ ...p, fat100: e.target.value }))} placeholder="e.g. 3.6" inputMode="decimal" className="w-full bg-input border border-border rounded px-2 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-2xs text-muted-foreground block mb-1">Serving size (g)</label>
                    <input type="number" value={manualFood.servingSize} onChange={(e) => setManualFood((p) => ({ ...p, servingSize: e.target.value }))} placeholder="100" inputMode="numeric" className="w-full bg-input border border-border rounded px-2 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-2xs text-muted-foreground block mb-1">Unit</label>
                    <input type="text" value={manualFood.servingUnit} onChange={(e) => setManualFood((p) => ({ ...p, servingUnit: e.target.value }))} placeholder="g" className="w-full bg-input border border-border rounded px-2 py-2 text-xs text-foreground focus:outline-none focus:border-primary/50" />
                  </div>
                </div>
                <button
                  onClick={handleSaveManual}
                  disabled={isSavingManual || !manualFood.name.trim() || !manualFood.cal100}
                  className="w-full btn-forge-primary py-2.5 rounded-md text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isSavingManual ? 'SAVING...' : 'SAVE TO HUNTER\'S LIST'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Favourites */}
        {tab === 'favourites' && (
          <div className="p-4 space-y-3">
            {favourites.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-3xl mb-3">⭐</div>
                <div className="text-sm font-bold text-foreground mb-1">No Favourites Yet</div>
                <div className="text-xs text-muted-foreground">
                  Star foods in Hunter&apos;s List or save AI search results to build your favourites.
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-forge">
                {favourites.map((fav) => (
                  <div key={fav.id} className="flex items-center justify-between rounded-lg p-3 hover:bg-card-elevated transition-colors border border-transparent hover:border-border/50">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{fav.foodName}</div>
                      <div className="text-2xs text-muted-foreground font-tabular">
                        {fav.calPerServing} kcal · P: {fav.proteinPerServing}g · {fav.servingSizeG}{fav.servingUnit}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <button
                        onClick={() => handleAddFavourite(fav)}
                        className="btn-forge-primary px-3 py-1.5 rounded text-xs font-bold"
                      >
                        + ADD
                      </button>
                      <button
                        onClick={() => handleRemoveFavourite(fav.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Icon name="TrashIcon" size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}